/**
 * Turns a spreadsheet selection pasted as TSV into supplier material rows.
 *
 * Pure: no React, no network, no i18n. A row that cannot be read comes back as
 * an error carrying what was typed, so the preview can show the user their own
 * text beside the reason.
 */

/** The server refuses a larger paste too; see `MAX_BULK_MATERIALS`. */
export const MAX_PASTE_ROWS = 500

const MAX_NAME_LENGTH = 255
const MAX_UNIT_LENGTH = 32
/** `DecimalField(max_digits=12, decimal_places=2)`. */
const MAX_AMOUNT = 9999999999.99

/**
 * What a spreadsheet puts around a figure rather than in it. `\s` already
 * covers the non-breaking space Excel writes between a symbol and its amount.
 */
const MONEY_DECORATION = /[$€£\s,]/g

/**
 * Dot-grouped digits closed by a comma and one or two decimals — `1.234,56`.
 * This is the one European figure that cannot also be read the American way: a
 * thousands group is always three digits, so `234,56` is not one. Read the
 * other way round it is `1.23`, a silent 1000x understatement.
 */
const EUROPEAN_GROUPED = /^-?\d{1,3}(\.\d{3})+,\d{1,2}$/

/** A figure as a spreadsheet writes one, once the decoration is gone. */
const NUMERIC_LITERAL = /^-?(\d+(\.\d*)?|\.\d+)$/

export type MaterialPasteErrorReason =
  | "emptyName"
  | "nameTooLong"
  | "unitTooLong"
  | "missingSalePrice"
  | "noSalePriceColumn"
  | "noMaterialNameColumn"
  | "salePriceNotANumber"
  | "costNotANumber"
  | "negativeAmount"
  | "amountTooLarge"

/** Why the paste as a whole yields nothing, said once above the preview. */
export type MaterialPasteBlockedReason =
  | "noSalePriceColumn"
  | "noMaterialNameColumn"
  | "nothingImportable"

export interface MaterialPasteBlocked {
  reason: MaterialPasteBlockedReason
  /** The headings row 1 was read as, so the message can name them back. */
  headings: string[]
}

export interface ParsedMaterialRow {
  /** 1-based position of the row in the paste, header and blanks included. */
  line: number
  name: string
  unit: string | null
  unit_cost: string | null
  unit_sale_price: string
}

export interface MaterialPasteRowError {
  line: number
  reason: MaterialPasteErrorReason
  /** Exactly what was pasted, for the preview — never the cleaned value. */
  name: string
  unit: string | null
  unitCost: string | null
  unitSalePrice: string | null
}

export interface MaterialPasteResult {
  rows: ParsedMaterialRow[]
  errors: MaterialPasteRowError[]
  /**
   * Set only when not one row can be imported. A per-row reason has nowhere to
   * show itself when the preview holds no rows at all, so the same answer is
   * carried here for a message above the table.
   */
  blocked: MaterialPasteBlocked | null
  /** Row 1 read as column headings and left out. */
  headerSkipped: boolean
  /** Earlier rows dropped because a later one repeated their name. */
  duplicateCount: number
  /** Rows past `MAX_PASTE_ROWS`, dropped before parsing. */
  droppedForCap: number
  /** At least one row held more than four columns; the rest were ignored. */
  extraColumnsIgnored: boolean
  /** At least one row held exactly two columns, so its figure is the sale price. */
  twoColumnRows: boolean
}

/**
 * Excel writes a second `text/html` flavour of the same selection. It is
 * styled markup with merged cells and no stable shape, so only the plain-text
 * TSV is read.
 */
export function pastedPlainText(data: DataTransfer | null | undefined): string {
  return data?.getData("text/plain") ?? ""
}

/**
 * Splits TSV into cells, honouring the RFC4180-ish quoting Excel applies to a
 * cell holding a tab, a newline or a leading quote. `split("\t")` corrupts
 * exactly those cells, which is why this is a state machine.
 */
export function tokenizePastedGrid(text: string): string[][] {
  const grid: string[][] = []
  let row: string[] = []
  let cell = ""
  let inQuotes = false
  let cellStarted = false

  const endCell = () => {
    row.push(cell)
    cell = ""
    cellStarted = false
  }

  const endRow = () => {
    endCell()
    grid.push(row)
    row = []
  }

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]

    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          cell += '"'
          index += 1
          continue
        }
        inQuotes = false
        continue
      }
      cell += char
      continue
    }

    // A quote only opens a quoted cell at the very start of one; anywhere else
    // it is a character the user typed (an inch mark, most often here).
    if (char === '"' && !cellStarted) {
      inQuotes = true
      cellStarted = true
      continue
    }

    if (char === "\t") {
      endCell()
      continue
    }

    if (char === "\r" || char === "\n") {
      if (char === "\r" && text[index + 1] === "\n") index += 1
      endRow()
      continue
    }

    cellStarted = true
    cell += char
  }

  // A paste ending in a newline has already closed its last row; anything left
  // is a final row with no terminator.
  if (cell !== "" || row.length > 0 || inQuotes) endRow()

  return grid
}

const isBlankRow = (cells: string[]) =>
  cells.every((cell) => cell.trim() === "")

/**
 * Reads a money cell the way a spreadsheet writes one: a currency symbol,
 * spaces and thousands separators are decoration.
 *
 * A comma is a thousands separator unless the cell is written the one European
 * way that admits no other reading (see `EUROPEAN_GROUPED`). `1,234` on its own
 * is genuinely ambiguous and guessing wrong is a 1000x price error, so it stays
 * American — a GCC pasting `1,23` gets 123, which the preview shows before
 * anything is saved.
 */
function parseAmount(
  raw: string
): { value: string } | { error: "notANumber" | "negative" | "tooLarge" } {
  const trimmed = raw.replace(/[$€£\s]/g, "")
  const cleaned = EUROPEAN_GROUPED.test(trimmed)
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : raw.replace(MONEY_DECORATION, "")
  if (!NUMERIC_LITERAL.test(cleaned)) return { error: "notANumber" }

  const numeric = Number(cleaned)
  if (!Number.isFinite(numeric)) return { error: "notANumber" }
  // Zero is a real price — a material given away, or one whose cost the GCC
  // deliberately records as nothing.
  if (numeric < 0) return { error: "negative" }
  if (numeric > MAX_AMOUNT) return { error: "tooLarge" }

  // The column takes two decimals, so the figure sent is the figure the
  // preview shows rather than something the server would refuse.
  return { value: toCents(cleaned) }
}

/**
 * Half-up to two decimals, on the digits rather than on a float: a price list
 * of three-decimal unit costs lands on a half-cent often enough that
 * `Math.round(1.005 * 100)` reading as 1.00 is a figure, not a formatting
 * detail.
 */
function toCents(cleaned: string): string {
  const negative = cleaned.startsWith("-")
  const [whole, fraction = ""] = (negative ? cleaned.slice(1) : cleaned).split(".")
  const padded = `${fraction}000`.slice(0, 3)
  let cents = BigInt(`${whole || "0"}${padded.slice(0, 2)}`)
  if (Number(padded[2]) >= 5) cents += 1n
  const digits = cents.toString().padStart(3, "0")
  return `${negative ? "-" : ""}${digits.slice(0, -2)}.${digits.slice(-2)}`
}

/**
 * Whether a cell is *written* as a figure — deliberately not whether it is an
 * acceptable one. A negative or oversized cost is still data, and reading it
 * as a column heading would swallow the row instead of reporting it.
 */
const looksLikeAFigure = (raw: string) =>
  EUROPEAN_GROUPED.test(raw.replace(/[$€£\s]/g, "")) ||
  NUMERIC_LITERAL.test(raw.replace(MONEY_DECORATION, ""))

interface CellLayout {
  name: string
  unitCost: string | null
  unitSalePrice: string | null
  unit: string | null
}

type ColumnRole = "name" | "unit" | "cost" | "price"

type HeaderMap = Partial<Record<ColumnRole, number>>

/**
 * What a heading can be called, most specific role first.
 *
 * Order matters for the containment pass below: "unit cost" and "unit sale
 * price" both contain "unit", so cost and price have to be claimed before the
 * unit column is, or every money heading would read as the unit.
 */
const HEADER_KEYWORDS: [ColumnRole, string[]][] = [
  ["cost", ["cost", "unit cost", "our cost", "buy"]],
  ["price", ["price", "sale price", "unit sale price", "sell", "retail"]],
  ["name", ["material", "name", "item", "description"]],
  ["unit", ["unit", "uom"]]
]

/** Lower-cased, punctuation-free and single-spaced: `Unit Cost ($)` → `unit cost`. */
const normalizeHeading = (raw: string) =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()

function headingRole(raw: string): ColumnRole | null {
  const heading = normalizeHeading(raw)
  if (!heading) return null

  const exact = HEADER_KEYWORDS.find(([, words]) => words.includes(heading))
  if (exact) return exact[0]

  const partial = HEADER_KEYWORDS.find(([, words]) =>
    words.some((word) => heading.includes(word))
  )
  return partial ? partial[0] : null
}

/**
 * Row 1 is a heading row when something in it is recognisably a column name
 * and nothing in it is money.
 *
 * The older rule — "the second cell is not a figure" — cannot work now that
 * column two is legitimately `Unit`, which is text on every data row.
 */
function readHeaderRow(cells: string[]): HeaderMap | null {
  const roles = cells.map(headingRole)
  if (!roles.some(Boolean)) return null
  if (cells.some((cell) => cell.trim() !== "" && looksLikeAFigure(cell))) {
    return null
  }

  const map: HeaderMap = {}
  roles.forEach((role, index) => {
    // A repeated heading keeps the leftmost column; the rest are ignored.
    if (role && map[role] === undefined) map[role] = index
  })
  return map
}

const at = (cells: string[], index: number | undefined) =>
  index === undefined ? null : (cells[index] ?? null)

/** Headings win over position, so the columns may arrive in any order. */
const readByHeader = (cells: string[], map: HeaderMap): CellLayout => ({
  name: at(cells, map.name) ?? "",
  unit: at(cells, map.unit),
  unitCost: at(cells, map.cost),
  unitSalePrice: at(cells, map.price)
})

/**
 * `name, unit, cost, price` — the order of the reviewed mockup.
 *
 * Three columns are genuinely ambiguous between `name, cost, price` and
 * `name, unit, price`, so the middle cell decides: money is a cost, anything
 * else is a unit. Two columns are read as name and SALE price — a price list a
 * GCC keeps for what it charges is the common short paste — but only when the
 * second cell is money; a name beside a unit has no price to import at all.
 */
function readPositionally(cells: string[]): CellLayout {
  const [first = "", second = "", third = "", fourth = ""] = cells

  if (cells.length >= 4) {
    return {
      name: first,
      unit: second,
      unitCost: third,
      unitSalePrice: fourth
    }
  }

  if (cells.length === 3) {
    return looksLikeAFigure(second)
      ? { name: first, unit: null, unitCost: second, unitSalePrice: third }
      : { name: first, unit: second, unitCost: null, unitSalePrice: third }
  }

  if (cells.length === 2) {
    return looksLikeAFigure(second)
      ? { name: first, unit: null, unitCost: null, unitSalePrice: second }
      : { name: first, unit: second, unitCost: null, unitSalePrice: null }
  }

  return { name: first, unit: null, unitCost: null, unitSalePrice: null }
}

function toError(
  line: number,
  layout: CellLayout,
  reason: MaterialPasteErrorReason
): MaterialPasteRowError {
  return {
    line,
    reason,
    name: layout.name,
    unit: layout.unit,
    unitCost: layout.unitCost,
    unitSalePrice: layout.unitSalePrice
  }
}

function parseRow(
  line: number,
  cells: string[],
  header: HeaderMap | null
): { row: ParsedMaterialRow } | { error: MaterialPasteRowError } {
  const layout = header ? readByHeader(cells, header) : readPositionally(cells)

  // Headings the user kept but no price among them: say so instead of
  // mis-reading some other column as the money that matters most.
  if (header && header.price === undefined) {
    return { error: toError(line, layout, "noSalePriceColumn") }
  }

  // Same again for the name: the rows do have names, it is the column that was
  // never recognised, and "no material name" would send the user to their data.
  if (header && header.name === undefined) {
    return { error: toError(line, layout, "noMaterialNameColumn") }
  }

  const name = layout.name.trim()
  if (!name) return { error: toError(line, layout, "emptyName") }
  if (name.length > MAX_NAME_LENGTH) {
    return { error: toError(line, layout, "nameTooLong") }
  }

  const unit = layout.unit?.trim() || null
  if (unit && unit.length > MAX_UNIT_LENGTH) {
    return { error: toError(line, layout, "unitTooLong") }
  }

  const rawSale = layout.unitSalePrice?.trim() ?? ""
  if (!rawSale) return { error: toError(line, layout, "missingSalePrice") }

  const sale = parseAmount(rawSale)
  if ("error" in sale) {
    return {
      error: toError(
        line,
        layout,
        sale.error === "notANumber"
          ? "salePriceNotANumber"
          : sale.error === "negative"
            ? "negativeAmount"
            : "amountTooLarge"
      )
    }
  }

  // An empty cost cell is the pass-through case, not a mistake.
  const rawCost = layout.unitCost?.trim() ?? ""
  let unitCost: string | null = null
  if (rawCost) {
    const cost = parseAmount(rawCost)
    if ("error" in cost) {
      return {
        error: toError(
          line,
          layout,
          cost.error === "notANumber"
            ? "costNotANumber"
            : cost.error === "negative"
              ? "negativeAmount"
              : "amountTooLarge"
        )
      }
    }
    unitCost = cost.value
  }

  return {
    row: {
      line,
      name,
      unit,
      unit_cost: unitCost,
      unit_sale_price: sale.value
    }
  }
}

/**
 * Within one paste a repeated name is the later row correcting the earlier —
 * the same rule the server applies — and the row keeps the later position.
 */
function dedupeLastWins(rows: ParsedMaterialRow[]): {
  rows: ParsedMaterialRow[]
  duplicateCount: number
} {
  const lastLineByName = new Map<string, number>()
  rows.forEach((row) => lastLineByName.set(row.name.toLowerCase(), row.line))

  const kept = rows.filter(
    (row) => lastLineByName.get(row.name.toLowerCase()) === row.line
  )
  return { rows: kept, duplicateCount: rows.length - kept.length }
}

function whyNothingImportable(
  header: HeaderMap | null,
  headerCells: string[]
): MaterialPasteBlocked {
  const headings = headerCells
    .map((cell) => cell.trim())
    .filter((cell) => cell !== "")

  if (header && header.price === undefined) {
    return { reason: "noSalePriceColumn", headings }
  }
  if (header && header.name === undefined) {
    return { reason: "noMaterialNameColumn", headings }
  }
  return { reason: "nothingImportable", headings }
}

export function parseMaterialPaste(text: string): MaterialPasteResult {
  const grid = tokenizePastedGrid(text)

  const lines = grid
    .map((cells, index) => ({ line: index + 1, cells }))
    .filter(({ cells }) => !isBlankRow(cells))

  // Users paste the heading row constantly, and it is the best thing in the
  // paste: it says which column is which, so nothing has to be inferred from
  // position at all.
  const firstCells = lines[0]?.cells ?? []
  const header = lines[0] ? readHeaderRow(firstCells) : null
  const headerSkipped = header !== null
  const dataLines = headerSkipped ? lines.slice(1) : lines

  const capped = dataLines.slice(0, MAX_PASTE_ROWS)
  const droppedForCap = dataLines.length - capped.length

  const parsedRows: ParsedMaterialRow[] = []
  const errors: MaterialPasteRowError[] = []
  let extraColumnsIgnored = false
  let twoColumnRows = false

  capped.forEach(({ line, cells }) => {
    // Both notices are about reading a row by position; headings say outright
    // which columns count, so nothing is being guessed at or thrown away.
    if (!header && cells.length > 4) extraColumnsIgnored = true
    if (!header && cells.length === 2 && looksLikeAFigure(cells[1] ?? "")) {
      twoColumnRows = true
    }

    const parsed = parseRow(line, cells, header)
    if ("error" in parsed) errors.push(parsed.error)
    else parsedRows.push(parsed.row)
  })

  const { rows, duplicateCount } = dedupeLastWins(parsedRows)

  return {
    rows,
    errors,
    blocked: rows.length
      ? null
      : whyNothingImportable(header, headerSkipped ? firstCells : []),
    headerSkipped,
    duplicateCount,
    droppedForCap,
    extraColumnsIgnored,
    twoColumnRows
  }
}
